import React, { useState } from 'react';
import { Box, Modal, Typography, Stack, Table, TableBody, TableRow, TableCell, IconButton } from '@mui/material';
import * as yup from 'yup';
import { User } from '@typing/global';
import './style.css';
import { useUpdateUserMutation } from '../../API/Dashboard';
import { failed } from '@components/Common/Toastify';
import { Close } from '@mui/icons-material';
import DateSelector from '@components/Common/DateSelector';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import TextInput from '@components/Common/TextInput';
import Button from '@components/Common/Button';

interface Props {
  isOpen: boolean;
  title?: string;
  data: User;
  readOnly: boolean;
  onClose: () => void;
  refetch: () => void;
}

const EditUser = ({ isOpen, title, data, onClose, refetch, readOnly }: Props) => {
  const { mutate: updateUser, isLoading } = useUpdateUserMutation({
    onSuccess: () => {
      refetch(); // Refresh the data after the update
      onClose(); // Close the modal
    },
    onError: (error: any) => {
      failed(error?.response?.data?.message);
    },
  });

  const formik = useFormik({
    initialValues: {
      firstName: data?.firstname || '',
      lastName: data?.lastname || '',
      email: data?.email || '',
      companyName: data?.companyName || '',
      phone: data?.phone || '',
      isVerified: data?.isVerified || false,
    },
    validationSchema: yup.object({
      firstName: yup.string().required('First Name is required'),
      lastName: yup.string(),
      email: yup.string(),
      companyName: yup.string(),
      phone: yup.string(),  
      isVerified: yup.boolean(),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      updateUser({
        id: data.id,
        updateData: values,
      });
    },
  });

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="custom-modal"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 20,
          }}
        >
          <Close />
        </IconButton>
        <Typography className="title-heading" id="modal-modal-title" variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>

        {readOnly ? (
          // View Mode
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>First Name:</strong>
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{data.firstname}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Last Name:</strong>
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{data.lastname}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Email:</strong>
                </TableCell>
                <TableCell>{data.email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Company Name:</strong>
                </TableCell>
                <TableCell>{data.companyName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Phone Number:</strong>
                </TableCell>
                <TableCell>{data.phone}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Verified:</strong>
                </TableCell>
                <TableCell>{data.isVerified ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          // Edit Mode
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <TextInput keyField={'firstName'} label={'First Name'} formik={formik} placeholder={'First Name'} />
              <TextInput keyField={'lastName'} label={'Last Name'} formik={formik} placeholder={'Last Name'} />
              <TextInput
                keyField={'email'}
                disabled
                type="email"
                label={'Email'}
                required={false}
                formik={formik}
                placeholder={'Email'}
              />
              {/* <DateSelector
                keyField={'dob'}
                label={'Date of Birth'}
                formik={formik}
                minDate={dayjs().subtract(120, 'years')}
                required={false}
                readOnly
                maxDate={dayjs()}
              /> */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="secondary" onClick={onClose} className="cancel-update-btn">
                  Cancel
                </Button>
                <Button type="submit" className="edit-update-btn">
                  Update
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Box>
    </Modal>
  );
};

export default EditUser;
